import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ShieldCheck, Cookie, ArrowLeft } from 'lucide-react';
import { useSection } from '../contexts/ContentContext';

const PAGES = {
  mentions: { key: 'mentions', label: 'Mentions légales', icon: FileText, path: '/mentions-legales' },
  privacy: { key: 'privacy', label: 'Confidentialité (RGPD)', icon: ShieldCheck, path: '/politique-confidentialite' },
  cookies: { key: 'cookies', label: 'Cookies', icon: Cookie, path: '/politique-cookies' },
};

export default function LegalPage({ pageKey }) {
  const legal = useSection('legal');
  const page = legal?.[pageKey] || {};
  const meta = PAGES[pageKey];

  return (
    <div className="bg-gradient-to-b from-sky-50 via-white to-white" data-testid={`legal-page-${pageKey}`}>
      {/* Hero */}
      <section className="relative pt-28 pb-12 sm:pt-32 sm:pb-16 border-b border-sky-100 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(56,189,248,0.08),transparent_60%),radial-gradient(circle_at_80%_60%,rgba(16,185,129,0.06),transparent_55%)]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-sky-700 hover:text-sky-900 mb-4">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white shadow-sm border border-sky-100 text-xs font-semibold text-sky-700 uppercase tracking-wider mb-4">
            {meta && <meta.icon className="w-3.5 h-3.5" />}
            Informations légales
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-sky-950 leading-tight">
            {page.title || meta?.label}
          </h1>
          {page.updatedAt && (
            <p className="mt-4 text-sm text-slate-500">Dernière mise à jour : <span className="font-semibold text-slate-700">{page.updatedAt}</span></p>
          )}
        </div>
      </section>

      {/* Navigation entre pages légales */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap gap-2">
          {Object.values(PAGES).map((p) => {
            const active = p.key === pageKey;
            return (
              <Link
                key={p.key}
                to={p.path}
                data-testid={`legal-nav-${p.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  active
                    ? 'bg-sky-950 text-white border-sky-950 shadow-sm'
                    : 'bg-white text-sky-800 border-sky-200 hover:border-sky-400 hover:text-sky-950'
                }`}
              >
                <p.icon className="w-4 h-4" />
                {p.label}
              </Link>
            );
          })}
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <article
          className="legal-content prose prose-slate max-w-none"
          data-testid="legal-content"
          dangerouslySetInnerHTML={{ __html: page.content || '<p>Contenu non disponible.</p>' }}
        />
      </section>
    </div>
  );
}
